import router from '@/presentation/router';

const FALLBACK_GA_MEASUREMENT_ID = 'G-RTQET4NG6P';
const GTAG_SCRIPT_ID = 'newty-gtag';

type Analytics = {
	trackEvent: (eventName: string, params?: Record<string, unknown>) => void;
	trackPageView: () => void;
};

function normalizeUrl(value?: unknown) {
	return String(value || '').replace(/\/$/, '');
}

function getMeasurementId() {
	return String(import.meta.env.VITE_GA_MEASUREMENT_ID || FALLBACK_GA_MEASUREMENT_ID).trim();
}

function ensureDataLayer() {
	window.dataLayer = window.dataLayer || [];
	window.gtag = window.gtag || ((...args: unknown[]) => window.dataLayer.push(args));
}

function injectGtagScript(measurementId: string) {
	if (document.getElementById(GTAG_SCRIPT_ID)) return;

	const script = document.createElement('script');
	script.id = GTAG_SCRIPT_ID;
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
	document.head.appendChild(script);
}

function loadGtag(measurementId: string) {
	if (window.__newtyGtagLoaded) return;

	window.__newtyGtagLoaded = true;
	window.gtag('js', new Date());
	window.gtag('config', measurementId, {
		send_page_view: false,
	});

	injectGtagScript(measurementId);
}

function scheduleGtagLoad(measurementId: string) {
	if (window.requestIdleCallback) {
		window.requestIdleCallback(() => loadGtag(measurementId), { timeout: 1500 });
		return;
	}

	window.setTimeout(() => loadGtag(measurementId), 0);
}

function createAnalytics(measurementId: string): Analytics {
	const trackEvent: Analytics['trackEvent'] = (eventName, params = {}) => {
		window.gtag('event', eventName, {
			send_to: measurementId,
			...params,
		});
	};

	const trackPageView: Analytics['trackPageView'] = () => {
		const currentRoute = router.currentRoute.value;
		const pagePath = currentRoute.fullPath || window.location.pathname;

		if (window.__newtyLastAnalyticsPath === pagePath) return;
		window.__newtyLastAnalyticsPath = pagePath;

		trackEvent('page_view', {
			page_title: document.title,
			page_location: window.location.href,
			page_path: `/app${pagePath.startsWith('/') ? pagePath : `/${pagePath}`}`,
		});
	};

	return { trackEvent, trackPageView };
}

function bindClickTracking(analytics: Analytics) {
	if (window.__newtyAnalyticsClickTrackingBound) return;
	window.__newtyAnalyticsClickTrackingBound = true;

	const dashboardBaseUrl = normalizeUrl(import.meta.env.VITE_DASHBOARD_BASE_URL || `${window.location.origin}/app`);

	document.addEventListener(
		'click',
		(event) => {
			const target = event.target;
			if (!(target instanceof Element)) return;

			const link = target.closest<HTMLAnchorElement>('a[href]');
			if (!link) return;

			const url = new URL(link.href, window.location.href);
			const isDashboardLink = Boolean(dashboardBaseUrl && url.href.startsWith(dashboardBaseUrl));
			const isOutboundLink = url.origin !== window.location.origin;
			const isDownloadLink = /\.(apk|dmg|exe|zip|pdf)$/i.test(url.pathname);

			if (!isDashboardLink && !isOutboundLink && !isDownloadLink) return;

			analytics.trackEvent('cta_click', {
				link_url: url.href,
				link_text: link.textContent?.trim().slice(0, 120) || link.getAttribute('aria-label') || '',
				link_domain: url.hostname,
				link_type: isDownloadLink ? 'download' : isDashboardLink ? 'dashboard' : 'outbound',
			});
		},
		{ capture: true },
	);
}

export default {
	name: 'gtag',
	enforce: 'post',
	setup(app) {
		const measurementId = getMeasurementId();
		if (!measurementId) return;

		ensureDataLayer();

		const analytics = createAnalytics(measurementId);
		app.config.globalProperties.$analytics = analytics;
		app.provide('analytics', analytics);

		bindClickTracking(analytics);
		scheduleGtagLoad(measurementId);
		analytics.trackPageView();

		router.afterEach(() => {
			window.setTimeout(analytics.trackPageView, 0);
		});
	},
};
